"use client"

import { useEffect, useId, useRef, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import { MagnifyingGlass, XMark, ArrowRight } from "@medusajs/icons"
import {
  productSearchResponseSchema,
  type ProductSearchItem,
} from "@kss/storefront-config"
import { convertToLocale } from "@lib/util/money"
import Thumbnail from "@modules/products/components/thumbnail"
import Link from "@modules/common/components/localized-client-link"
import { searchProductHref } from "@lib/search/links"

export default function EquipmentSearch({
  initialQuery = "",
  compact = false,
}: {
  initialQuery?: string
  compact?: boolean
}) {
  const [query, setQuery] = useState(initialQuery)
  const [items, setItems] = useState<ProductSearchItem[]>([])
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  )
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [retry, setRetry] = useState(0)
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const { countryCode } = useParams<{ countryCode: string }>()
  const router = useRouter()
  const normalized = query.trim()
  useEffect(() => {
    setQuery(initialQuery)
  }, [initialQuery])
  useEffect(() => {
    if (!open || normalized.length < 2) {
      setItems([])
      setStatus("idle")
      return
    }
    const controller = new AbortController()
    setStatus("loading")
    setItems([])
    setActive(-1)
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/search/products?q=${encodeURIComponent(
            normalized
          )}&mode=autocomplete`,
          { signal: controller.signal, cache: "no-store" }
        )
        if (!response.ok) throw new Error("Search unavailable")
        const result: unknown = await response.json()
        const data = productSearchResponseSchema.parse(result)
        if (!controller.signal.aborted) {
          setItems(data.items)
          setStatus("ready")
        }
      } catch {
        if (!controller.signal.aborted) setStatus("error")
      }
    }, 250)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [normalized, open, retry])
  useEffect(() => {
    if (active >= 0)
      document
        .getElementById(`${id}-option-${active}`)
        ?.scrollIntoView({ block: "nearest" })
  }, [active, id])
  const visible = open && normalized.length >= 2
  return (
    <div
      className={`equipment-search ${
        compact ? "equipment-search-compact" : ""
      }`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false)
          setActive(-1)
        }
      }}
    >
      <form
        role="search"
        action={`/${countryCode}/buscar`}
        onSubmit={(event) => {
          if (active >= 0 && items[active]) {
            event.preventDefault()
            router.push(`/${countryCode}${searchProductHref(items[active])}`)
          }
          setOpen(false)
        }}
      >
        <MagnifyingGlass aria-hidden="true" />
        <label htmlFor={id} className="sr-only">
          Buscar productos
        </label>
        <input
          id={id}
          ref={inputRef}
          name="q"
          type="search"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          value={query}
          maxLength={160}
          placeholder="Busca un producto, equipo o modelo…"
          aria-expanded={visible}
          aria-controls={visible ? `${id}-results` : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            visible && active >= 0 ? `${id}-option-${active}` : undefined
          }
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault()
              setOpen(false)
              setActive(-1)
            }
            if (
              (event.key === "ArrowDown" || event.key === "ArrowUp") &&
              items.length
            ) {
              event.preventDefault()
              setOpen(true)
              setActive((value) =>
                event.key === "ArrowDown"
                  ? (value + 1) % items.length
                  : value <= 0
                  ? items.length - 1
                  : value - 1
              )
            }
          }}
        />
        {query && (
          <button
            type="button"
            className="equipment-icon-button"
            aria-label="Limpiar búsqueda"
            onClick={() => {
              setQuery("")
              setActive(-1)
              inputRef.current?.focus()
            }}
          >
            <XMark aria-hidden="true" />
          </button>
        )}
        <button
          type="submit"
          className="equipment-search-submit"
          aria-label="Buscar"
        >
          <ArrowRight aria-hidden="true" />
        </button>
      </form>
      {visible && (
        <div className="equipment-search-suggestions">
          <div role="status" className="equipment-search-status">
            {status === "loading"
              ? "Buscando en el catálogo…"
              : status === "error"
              ? "No pudimos cargar las sugerencias."
              : items.length
              ? "Productos sugeridos"
              : "No encontramos coincidencias. Prueba con otra palabra."}
          </div>
          <ul
            id={`${id}-results`}
            role="listbox"
            aria-label="Productos sugeridos"
          >
            {items.map((item, index) => (
              <li key={item.id} role="presentation">
                <Link
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={active === index}
                  href={searchProductHref(item)}
                  onClick={() => setOpen(false)}
                  className="equipment-search-suggestion"
                >
                  <Thumbnail
                    thumbnail={item.thumbnail}
                    size="square"
                    className="equipment-search-thumbnail"
                  />
                  <span>
                    <strong>{item.title}</strong>
                    {item.variantTitle && <small>{item.variantTitle}</small>}
                    <small>{item.inStock ? "Disponible" : item.stockUnverified ? "Disponibilidad por confirmar" : "Sin stock"}</small>
                  </span>
                  <b>
                    {item.price === null
                      ? "Ver producto"
                      : convertToLocale({
                          amount: item.price,
                          currency_code: "clp",
                        })}
                  </b>
                </Link>
              </li>
            ))}
          </ul>
          {status === "error" && (
            <button
              className="equipment-text-link"
              type="button"
              onClick={() => setRetry((value) => value + 1)}
            >
              Reintentar búsqueda
            </button>
          )}
          <Link
            className="equipment-search-all"
            href={`/buscar?q=${encodeURIComponent(normalized)}`}
            onClick={() => setOpen(false)}
          >
            Ver todos los resultados <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  )
}
